import useI18n from '@hooks/i18n/useI18n';
import { useCheckoutCartState } from '@monofront/business/contexts';
import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import useHandlePaymentError from '../hooks/useHandlePaymentErrors';
import {
  ICreatePspTokenFromApplePayTokenQueryResult,
  ICreatePaymentIntentForNewApplePayCardQueryResult,
  PaymentMethodSource,
  MembershipPlan,
} from '@monofront/types';
import {
  CREATE_PAYMENT_INTENT_FOR_NEW_APPLE_PAY_CARD,
  CREATE_PSP_TOKEN_FROM_APPLE_PAY_TOKEN,
} from '@monofront/business/queries';
import { useMutation } from '@apollo/client';
import { useCheckoutPayment } from '../contexts';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import useAddPaymentMethod from '@features/PaymentMethods/useAddPaymentMethod';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import useCompleteOrder from '../hooks/useCompleteOrder';
import useApplePay from '../hooks/useApplePay';
import useApplePayEligibility from '../hooks/useApplePayEligibility';
import useSubscriptionPrice from '@hooks/useSubscriptionPrice';
import useLocalizedOrganizationInfos from '@hooks/i18n/useLocalizedOrganizationInfos';
import { useMembershipCouponContext } from '@context/membershipCoupon';
import { getEndOfFreeTrialDate } from '@monofront/business/utils';
import useApplePaySession from '../hooks/useApplePaySession';
import { useAuthentication } from '@context/businessContainer';

interface IPayWithApplePayCtaProps {
  type?: 'plain' | 'check-out';
}

/**
 * Apple Pay CTA Component
 *
 * This component handles the complete Apple Pay payment flow for new cards during checkout.
 * It integrates with Apple Pay JS API to process payments and create payment intents.
 *
 * APPLE PAY FLOW EXPLANATION:
 *
 * 1. USER INTERACTION:
 *    - User clicks the Apple Pay button
 *    - Component validates checkout cart exists
 *
 * 2. APPLE PAY SESSION CREATION:
 *    - Creates payment request payload with cart total
 *    - Initializes ApplePaySession(3, payload) - version 3 with our payment request
 *    - Sets up event handlers for the session lifecycle
 *
 * 3. MERCHANT VALIDATION:
 *    - Apple Pay calls onvalidatemerchant with validation URL
 *    - We validate our domain is authorized to use Apple Pay
 *    - This is Apple's security measure to prevent unauthorized domains
 *
 * 4. USER AUTHORIZATION:
 *    - Apple Pay shows native payment UI to user
 *    - User authenticates with Face ID/Touch ID and authorizes payment
 *    - Apple Pay calls onpaymentauthorized with payment token
 *
 * 5. TOKEN PROCESSING:
 *    - We receive an OPAQUE TOKEN from Apple (encrypted payment data)
 *    - This token contains sensitive card info but is encrypted and secure
 *    - We send this token to our backend via CREATE_PSP_TOKEN_FROM_APPLE_PAY_TOKEN
 *    - Backend decrypts and converts it to a PSP (Payment Service Provider) token
 *
 * 6. PAYMENT INTENT CREATION:
 *    - We use the PSP token to create a payment intent
 *    - This initiates the actual payment processing with our payment processor
 *    - Payment intent contains all necessary info for the transaction
 *
 * 7. ORDER COMPLETION:
 *    - If payment succeeds, we complete the Apple Pay session with SUCCESS
 *    - Optionally save the card for future use
 *    - Complete the order in our system
 *    - If payment fails, we complete session with FAILURE status
 *
 * 8. SESSION CLEANUP:
 *    - Apple Pay session ends
 *    - User sees success/failure feedback
 *    - Our UI updates accordingly
 *
 * KEY CONCEPTS:
 * - OPAQUE TOKEN: Encrypted payment data from Apple that we can't read directly
 * - PSP TOKEN: Decrypted, standardized token our payment processor can use
 * - PAYMENT INTENT: The actual payment request sent to our payment processor
 * - SESSION LIFECYCLE: Apple Pay manages the entire user experience, we just respond to events
 */

const PayWithApplePayCta = ({
  type = 'check-out',
}: IPayWithApplePayCtaProps) => {
  const { checkoutCart } = useCheckoutCartState();
  const { locale } = useI18n();
  const tAccount = useTranslations('Account');
  const tCart = useTranslations('Cart');
  const { handlePaymentError } = useHandlePaymentError();
  const { requestPayload } = useApplePay();
  const { isApplePayAvailable, isCheckingApplePayAvailability } =
    useApplePayEligibility();
  const { url } = useLocalizedOrganizationInfos();
  const { monthlySubscriptionPrice } = useSubscriptionPrice();
  const { discountedPrice } = useMembershipCouponContext();
  const { membershipPlan } = useCheckoutCartState();
  const isYearlySubscription = membershipPlan === MembershipPlan.YEARLY;
  const subscriptionPrice = isYearlySubscription
    ? discountedPrice
    : monthlySubscriptionPrice;

  const { completeOrder } = useCompleteOrder();
  const { isMember } = useAuthentication();
  const buttonRef = useRef<HTMLElement>(null);
  const { setIsOrderValidationInProgress, selectedNewApplePay } =
    useCheckoutPayment();
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);
  const [createPspTokenFromApplePayToken] =
    useMutation<ICreatePspTokenFromApplePayTokenQueryResult>(
      CREATE_PSP_TOKEN_FROM_APPLE_PAY_TOKEN,
    );
  const [createPaymentIntentForNewApplePayCard] =
    useMutation<ICreatePaymentIntentForNewApplePayCardQueryResult>(
      CREATE_PAYMENT_INTENT_FOR_NEW_APPLE_PAY_CARD,
    );
  const { handleAddNewCard } = useAddPaymentMethod(false, 'checkout');
  const paymentRequestPayload = requestPayload(checkoutCart?.total || 0);
  const enhancedPaymentRequestPayload: ApplePayJS.ApplePayPaymentRequest = {
    ...paymentRequestPayload,
    ...(!isMember && {
      recurringPaymentRequest: {
        managementURL: `${url}/pages/manage-my-subscription`,
        paymentDescription: 'Recurring payment',
        regularBilling: {
          label: tAccount('subscriptionRecurring'),
          amount: subscriptionPrice.toString(),
          paymentTiming: 'recurring',
          ...(isYearlySubscription && {
            recurringPaymentStartDate: new Date(getEndOfFreeTrialDate()),
          }),
          recurringPaymentIntervalUnit: isYearlySubscription ? 'year' : 'month',
        },
        ...(isYearlySubscription && {
          billingAgreement: tAccount('subscriptionBillingAgreement'),
          trialBilling: {
            label: tCart('MembershipProductCart.productName'),
            amount: '0.00',
            paymentTiming: 'recurring',
          },
        }),
      },
    }),
  };

  const shouldSaveCard =
    isSubscriptionAvailable || selectedNewApplePay?.shouldSaveCardForFutureUse;

  // ===== PAYMENT FLOW FUNCTIONS =====

  /**
   * Core payment validation and processing logic
   * This function handles the complete payment flow:
   * 1. Validates checkout cart data
   * 2. Creates PSP token from Apple Pay token
   * 3. Creates payment intent with the PSP token
   * 4. Processes the payment
   * 5. Handles card validation and order completion
   *
   * @param applePayToken - The payment token received from Apple Pay
   * @returns Promise<boolean> - True if payment was successful, false otherwise
   */
  const validateOrder = async (
    applePayToken: ApplePayJS.ApplePayPaymentToken,
  ) => {
    try {
      if (
        !checkoutCart?.id ||
        !checkoutCart?.payment?.id ||
        !selectedNewApplePay?.paymentMethodCode
      ) {
        throw new Error(
          'Try to pay with a new card with corrupted checkoutCart.',
        );
      }

      // Step 2: Create PSP token from Apple Pay token
      // This converts Apple Pay's secure token into a format our payment processor can use
      const { data: createPspTokenFromApplePayTokenData } =
        await createPspTokenFromApplePayToken({
          variables: {
            token: applePayToken.paymentData,
          },
        });

      if (
        !createPspTokenFromApplePayTokenData?.createPspTokenFromApplePayToken
          ?.token
      ) {
        throw new Error('No token generated for the user new apple pay card.');
      }

      // Step 3: Create payment intent with the PSP token
      // This initiates the actual payment processing
      const { data: createPaymentIntentForNewApplePayCardData } =
        await createPaymentIntentForNewApplePayCard({
          variables: {
            tokenValue: checkoutCart.id,
            cartPaymentId: checkoutCart.payment.id,
            temporaryPaymentMethodToken:
              createPspTokenFromApplePayTokenData
                ?.createPspTokenFromApplePayToken?.token,
            paymentMethodCode: selectedNewApplePay.paymentMethodCode,
            saveCardForFutureUse:
              isSubscriptionAvailable ||
              selectedNewApplePay.shouldSaveCardForFutureUse,
          },
        });

      if (
        !createPaymentIntentForNewApplePayCardData
          ?.createPaymentIntentForNewApplePayCard?.payment
      ) {
        throw new Error('Create Payment Intent failed with empty response.');
      }

      const { financialPaymentIntentId: paymentId } =
        createPaymentIntentForNewApplePayCardData
          .createPaymentIntentForNewApplePayCard.payment;

      // Step 5: Handle card validation and order completion
      await onApplePayCardValidation(paymentId);

      return true;
    } catch (error: any) {
      setIsOrderValidationInProgress(false);

      handlePaymentError({
        code: 'PAY_WITH_NEW_CARD_FAILED',
        message:
          !error?.graphQLErrors && error?.message
            ? error.message
            : 'Cannot create a PaymentIntent for a new card or complete an order for a non-3DS card.',
        shouldDisplayError: true,
        error,
        context: {
          selected_apple_pay_status: selectedNewApplePay,
        },
      });

      return false;
    }
  };

  const { createSession } = useApplePaySession({
    onPaymentAuthorized: validateOrder,
    paymentRequestPayload: enhancedPaymentRequestPayload,
  });

  /**
   * Handles post-payment card validation and order completion
   * This function is called after a successful payment to:
   * 1. Optionally save the new Apple Pay card to user's account
   * 2. Complete the order
   * 3. Update UI state
   *
   * @param paymentId - The payment ID returned from the payment intent creation
   */
  const onApplePayCardValidation = async (paymentId: string | undefined) => {
    if (shouldSaveCard) {
      try {
        if (!paymentId) {
          throw new Error(
            'Payment ID is required to attach a new apple pay card.',
          );
        }

        // Attach the new Apple Pay card to user's account
        await handleAddNewCard(paymentId, PaymentMethodSource.CHECKOUT);
      } catch (error: any) {
        handlePaymentError({
          code: 'ATTACH_WITH_NEW_APPLE_PAY_CARD_FAILED',
          message:
            !error?.graphQLErrors && error?.message
              ? error.message
              : 'Cannot attach a new apple pay card.',
          error,
          context: {
            selected_apple_pay_status: selectedNewApplePay,
          },
        });
      }
    }

    const hasCompletedOrder = await completeOrder();

    if (!hasCompletedOrder) {
      setIsOrderValidationInProgress(false);
    }
  };

  /**
   * Main Apple Pay button click handler
   * This function orchestrates the entire Apple Pay payment flow:
   * 1. Validates checkout cart
   * 2. Creates Apple Pay session
   * 3. Sets up event handlers for the session
   * 4. Begins the payment process
   */
  const onApplePayButtonClick = async () => {
    if (!checkoutCart) {
      handlePaymentError({
        code: 'CREATE_NEW_APPLE_PAY_REQUEST_FAILED',
        message:
          'Cannot proceed with Apple Pay payment without a checkout cart',
      });

      return null;
    }
    createSession();
  };

  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.addEventListener('click', onApplePayButtonClick);

      return () =>
        buttonRef.current?.removeEventListener('click', onApplePayButtonClick);
    }
  }, [onApplePayButtonClick]);

  if (isCheckingApplePayAvailability) {
    return <HeartLoader />;
  }

  if (!isApplePayAvailable) {
    return;
  }

  return (
    <>
      <apple-pay-button
        ref={buttonRef}
        buttonstyle="black"
        type={type}
        locale={locale}
      />
      <style jsx global>{`
        apple-pay-button {
          --apple-pay-button-border-radius: 100px;
          --apple-pay-button-height: 44px;
          --apple-pay-button-width: 100%;
        }
      `}</style>
    </>
  );
};

export default PayWithApplePayCta;
